import { NavigationMenu as KNavigationMenu } from '@kobalte/core/navigation-menu';
import { children, Show } from 'solid-js';
import { twJoin } from '@troon/tailwind-preset/merge';
import { IconCaretDownMd } from '@troon/icons/caret-down-md';
import { Dynamic } from 'solid-js/web';
import { Link } from './link';
import type { IconComponent } from '@troon/icons';
import type { ComponentProps, JSXElement, ParentProps } from 'solid-js';

type Props = ComponentProps<typeof KNavigationMenu> & {
	trigger: JSXElement;
	sameWidth?: boolean;
	showIcon?: boolean;
	noPortalPadding?: boolean;
};

export function ResponsiveNavigationMenu(props: Props) {
	return (
		<>
			<KNavigationMenu class="hidden lg:block">
				<KNavigationMenu.Menu>
					<div class="flex flex-col gap-2">{props.children}</div>
				</KNavigationMenu.Menu>
			</KNavigationMenu>
			<NavigationMenu class="lg:hidden" {...props} />
		</>
	);
}

export function NavigationMenu(props: Props) {
	return (
		<KNavigationMenu placement={props.placement} class={props.class} delayDuration={0} skipDelayDuration={300}>
			<KNavigationMenu.Menu sameWidth={props.sameWidth === false ? false : true} fitViewport orientation="horizontal">
				<NavigationMenuTrigger showIcon={props.showIcon}>{props.trigger}</NavigationMenuTrigger>
				<NavigationMenuPortal sameWidth={props.sameWidth} noPadding={props.noPortalPadding}>
					{props.children}
				</NavigationMenuPortal>
			</KNavigationMenu.Menu>
			<KNavigationMenu.Viewport class="group/viewport z-50 animate-out animation-duration-200 fade-out slide-out-to-top-12 ui-expanded:animate-in ui-expanded:slide-in-from-top-12" />
		</KNavigationMenu>
	);
}

type TriggerProps = ParentProps<{ showIcon?: boolean }>;

export function NavigationMenuTrigger(props: TriggerProps) {
	const content = children(() => props.children);
	return (
		<KNavigationMenu.Trigger
			class={twJoin(
				'group rounded-lg transition-all duration-200 outline-none',
				typeof content() === 'string'
					? 'flex w-full items-center justify-between border border-neutral bg-white px-4 py-3 focus-visible:ring-1 focus-visible:ring-brand-700'
					: 'flex items-center gap-x-2',
			)}
		>
			{content()}
			<Show when={props.showIcon !== false}>
				<KNavigationMenu.Icon class="transition-transform duration-200 ui-expanded:rotate-180">
					<IconCaretDownMd class="text-xl text-brand" />
				</KNavigationMenu.Icon>
			</Show>
		</KNavigationMenu.Trigger>
	);
}

type PortalProps = ParentProps<{ sameWidth?: boolean; noPadding?: boolean }>;

export function NavigationMenuPortal(props: PortalProps) {
	return (
		<KNavigationMenu.Portal>
			<KNavigationMenu.Content
				class={twJoin(
					'group/portal relative top-2 flex max-h-[var(--kb-popper-content-available-height)] flex-col gap-2 overflow-auto rounded-lg border border-neutral bg-white shadow-xl outline-none',
					props.noPadding ? '' : 'p-3',
					props.sameWidth !== false ? 'w-[var(--kb-popper-anchor-width)]' : 'min-w-72',
				)}
			>
				{props.children}
			</KNavigationMenu.Content>
		</KNavigationMenu.Portal>
	);
}

type ItemProps = ComponentProps<typeof KNavigationMenu.Item> & {
	icon?: IconComponent;
};

export function NavigationMenuItem(props: ItemProps) {
	return (
		<KNavigationMenu.Item
			as={Link}
			href={props.href}
			class={twJoin(
				'group relative flex flex-row items-center gap-2 overflow-hidden rounded-lg bg-white px-2 py-2.5 text-neutral-950 transition-colors duration-200 hover:bg-brand-100 focus-visible:ring-0 focus-visible:ring-offset-0 aria-current-page:bg-brand-100',
				props.class,
			)}
		>
			<span class="absolute inset-y-0 left-0 w-4 rounded-lg border-s-0 border-s-white transition-all duration-200 group-hover:border-s-brand-100 group-hover:bg-brand-100 group-data-[expanded]/viewport:hidden group-aria-current-page:block group-aria-current-page:border-s-4 group-aria-current-page:border-s-brand group-aria-current-page:bg-brand-100 group-data-[expanded]/viewport:group-aria-current-page:hidden" />
			<Dynamic component={props.icon} class="shrink-0 grow-0 text-lg text-black" />
			{props.children}
		</KNavigationMenu.Item>
	);
}
